import {createApp} from 'vue';
import {GET} from '../modules/fetch.ts';
import {fomanticQuery} from '../modules/fomantic/base.ts';
import {createElementFromHTML} from '../utils/dom.ts';
import {registerGlobalEventFunc} from '../modules/observer.ts';

export function initRepoPullRequestUpdate(el: HTMLElement) {
  const elDropdown = el.querySelector(':scope > .ui.dropdown');
  if (!elDropdown) return;
  const elButton = el.querySelector<HTMLButtonElement>(':scope > button')!;

  fomanticQuery(elDropdown).dropdown({
    onChange(_text: string, _value: string, $choice: any) {
      const choiceEl = $choice[0];
      elButton.textContent = choiceEl.textContent;
      elButton.setAttribute('data-url', choiceEl.getAttribute('data-update-url'));
    },
  });
}

function onCommitStatusChecksToggle(btn: HTMLElement) {
  const panel = btn.closest('.commit-status-toggle')!.parentElement!;
  const list = panel.querySelector<HTMLElement>('.commit-status-list')!;
  list.style.maxHeight = list.style.maxHeight ? '' : '0px'; // toggle
  btn.textContent = btn.getAttribute(list.style.maxHeight ? 'data-show-all' : 'data-hide-all');
}

// The merge box is fully replaced on each reload, so the status list would lose its collapsed
// state and scroll position, which makes a long list jump back to the top while CI is running.
// It returns a callback which restores the scroll position, it must be called after the new
// element has been attached to the document, otherwise "scrollTop" doesn't take effect.
export function keepCommitStatusListState(oldBox: Element, newBox: Element): () => void {
  const oldList = oldBox.querySelector<HTMLElement>('.commit-status-list');
  const newList = newBox.querySelector<HTMLElement>('.commit-status-list');
  if (!oldList || !newList) return () => {};

  newList.style.maxHeight = oldList.style.maxHeight;
  const oldToggle = oldBox.querySelector<HTMLElement>('.commit-status-toggle button');
  const newToggle = newBox.querySelector<HTMLElement>('.commit-status-toggle button');
  if (oldToggle && newToggle) newToggle.textContent = oldToggle.textContent;

  const scrollTop = oldList.scrollTop;
  return () => { newList.scrollTop = scrollTop };
}

async function initRepoPullRequestMergeForm(box: HTMLElement) {
  const el = box.querySelector('#pull-request-merge-form');
  if (!el) return;

  const data = JSON.parse(el.getAttribute('data-merge-form-props')!);
  const {default: PullRequestMergeForm} = await import('../components/PullRequestMergeForm.vue');
  const view = createApp(PullRequestMergeForm, {mergeFormProps: data});
  view.mount(el); // TODO: can unmount when reloaded?
}

export function initRepoPullMergeBox(el: HTMLElement) {
  registerGlobalEventFunc('click', 'onCommitStatusChecksToggle', onCommitStatusChecksToggle);
  initRepoPullRequestMergeForm(el);

  const reloadingIntervalValue = el.getAttribute('data-pull-merge-box-reloading-interval');
  if (!reloadingIntervalValue) return;

  const reloadingInterval = parseInt(reloadingIntervalValue);
  const pullLink = el.getAttribute('data-pull-link');
  let timerId: number | null;

  // The merge box has complex buttons & form, if the user has interacted with any element, don't refresh.
  // Otherwise, the user won't be able to merge or schedule a merge (auto-merge) when the PR status is not ready.
  let interacted = false;
  const interactionEvents = ['focusin', 'mousedown', 'click', 'keydown', 'input'];
  for (const event of interactionEvents) {
    el.addEventListener(event, () => { interacted = true }, {capture: true});
  }

  let reloadMergeBox: () => Promise<void>;
  const stopReloading = () => {
    if (!timerId) return;
    clearTimeout(timerId);
    timerId = null;
  };
  const startReloading = () => {
    if (timerId || interacted) return;
    timerId = window.setTimeout(reloadMergeBox, reloadingInterval);
  };
  const onVisibilityChange = () => {
    if (document.hidden) {
      stopReloading();
    } else {
      startReloading();
    }
  };
  reloadMergeBox = async () => {
    if (interacted) return;
    const resp = await GET(`${pullLink}/merge_box`);
    stopReloading();
    if (!resp.ok) {
      startReloading();
      return;
    }
    document.removeEventListener('visibilitychange', onVisibilityChange);

    const respText = (await resp.text()).trim();
    if (!respText) {
      el.remove(); // merge box might not exist if the PR has changed (e.g.: merged and the head branch has been deleted)
      return;
    }
    const newElem = createElementFromHTML(respText);
    const restoreStatusListScroll = keepCommitStatusListState(el, newElem);
    el.replaceWith(newElem);
    restoreStatusListScroll();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  startReloading();
}
